import {
    Category,
    CategoryChart,
    deltaValue,
    getBaseValue,
    KpiReport,
    LongText,
    roundValue,
    Unit
} from "../../Components/processors";
import { FilterMetadataApiWithCountReturn, WaterProvider } from "./types";
import _ from "lodash";
import { EMPTY_ARRAY, PROVIDER_TITLE_CLASS, VALUE_STATUS_NAMING } from "./constants";
import { figureDeltaString } from "../../Components/categoryChartQueryFns/figureDeltaString";
import { appMedian } from "./appMedian";
import { getConnectionSizeMedianText } from "../../utils";
import { extractInitialYearWith1DimValues } from "./extractInitialYear";

const providerTitleStartEndDiv = {
    providerTitleStartSection: `<div class="${PROVIDER_TITLE_CLASS}">`,
    providerTitleEndSection: `</div>`
};

export const getFigureAndSolidDashGraph = (
    metadata: FilterMetadataApiWithCountReturn,
    provider: WaterProvider,
    results: KpiReport[],
    kpis: string[][],
    categoryChart: CategoryChart,
    category: Category,
    defaultUnit: Unit,
    extractReportDataFn: (kpis: string[][], results: KpiReport[], year?: number) => KpiReport,
    years?: number[],
    colorIndex?: number
): {
    unit?: Unit;
    year?: number;

    figure: {
        header?: LongText;
        value: number | string;
        decimals: number;
        footer?: LongText;
    };

    graph?: {
        header?: LongText;
        years: number[];
        measure: string;
        // largeProviderAverageData: number[];

        providerMedianSeriesName: string;
        providerMedianAnnotation: string;
        providerMedianData: number[];

        // largeProviderAverageSeriesName: string;
        lineData: number[];
        lineDataSeriesName: string;
        footer?: LongText;
    };
} => {
    const providerResults = results.filter(d => d?.provider?.tid === provider?.tid);
    const yearToResults = _.groupBy(providerResults, d => d.year);
    // const yearToAllResults = _.groupBy(results, d => d.year);
    const yearProviderKeyFn = (year: number, tid: number) => `${year} %%% ${tid}`;

    const yearProviderToAllResults = _.groupBy(results, d => yearProviderKeyFn(d.year, d?.provider?.tid));

    const conSizeProviders = _.uniqBy(
        results.map(d => d?.provider).filter(d => d.connectionSizeCategory === provider?.connectionSizeCategory),
        d => d.tid
    );

    // if (!yearToResults || !_.keys(yearToResults ?? EMPTY_OBJECT)?.length) return null;

    let _years = years ?? ((_.orderBy(_.keys(yearToResults), d => d, "asc") ?? EMPTY_ARRAY) as number[]);
    const latestYear = _years?.[_years?.length - 1];
    const previousYear = _years?.[_years?.length - 2];
    // const initialYear = _years?.[0];
    // if (latestYear == null) return null;

    const { visType, figureHeader, figureFooter, visHeader, visFooter } = categoryChart;

    const latestYearData = extractReportDataFn(kpis, yearToResults?.[latestYear], latestYear);
    const previousYearData = extractReportDataFn(kpis, yearToResults?.[previousYear], previousYear);

    const {
        value: latestYearValue,
        valueStatus: latestYearValueStatus,
        unit: _unit
    } = !latestYearData
        ? ({} as KpiReport)
        : {
              ...latestYearData,
              ...getBaseValue(latestYearData?.value, latestYearData?.unit)
          };

    const { value: previousYearValue } = !previousYearData
        ? ({} as KpiReport)
        : {
              ...previousYearData,
              ...getBaseValue(previousYearData?.value, previousYearData?.unit)
          };

    let unit = _unit ?? defaultUnit;

    let figure: {
        header?: LongText;
        value: number | string;
        decimals: number;
        footer?: LongText;
    } = null;

    const providerExist = providerResults?.length > 0;
    if (!providerExist || latestYearValue == null) {
        const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
            ...providerTitleStartEndDiv,
            year: latestYear,
            value: null,
            deltaString: null,
            providerName: provider.name
        });

        const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
            ...providerTitleStartEndDiv,
            year: latestYear,
            value: null,
            deltaString: null,
            providerName: provider.name
        });

        figure = {
            header:
                !figureHeader || figureHeader.processed.trim() === ""
                    ? null
                    : {
                          processed: figureHeaderProcessed,
                          value: figureHeaderProcessed,
                          format: figureHeaderProcessed
                      },
            footer:
                !figureFooter || figureFooter.processed.trim() === ""
                    ? null
                    : {
                          processed: figureFooterProcessed,
                          value: figureFooterProcessed,
                          format: figureFooterProcessed
                      },
            value:
                latestYearValueStatus != null
                    ? VALUE_STATUS_NAMING?.[latestYearValueStatus] ?? latestYearValueStatus
                    : !providerExist
                    ? VALUE_STATUS_NAMING.NR
                    : previousYearValue != null && latestYearValue == null
                    ? VALUE_STATUS_NAMING.MD
                    : VALUE_STATUS_NAMING.NR,
            decimals: 0
        };
    } else {
        const delta = deltaValue(previousYearData?.value, latestYearValue, unit);
        const deltaString = figureDeltaString(delta, category, colorIndex);

        // create content for header and footer of the figure
        const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
            ...providerTitleStartEndDiv,
            year: latestYear,
            value: latestYearValue,
            deltaString,
            providerName: provider?.name
        });

        const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
            ...providerTitleStartEndDiv,
            year: latestYear,
            value: latestYearValue,
            deltaString,
            providerName: provider?.name
        });

        figure = {
            decimals: latestYearValue != null ? roundValue(latestYearValue, unit as Unit, true, false).decimals : 0,
            header:
                !figureHeader || figureHeader.processed.trim() === ""
                    ? null
                    : {
                          processed: figureHeaderProcessed,
                          value: figureHeaderProcessed,
                          format: figureHeaderProcessed
                      },
            value:
                latestYearValueStatus != null
                    ? VALUE_STATUS_NAMING?.[latestYearValueStatus] ?? latestYearValueStatus
                    : previousYearValue != null && latestYearValue == null
                    ? VALUE_STATUS_NAMING.MD
                    : latestYearValue == null
                    ? VALUE_STATUS_NAMING.NR
                    : roundValue(latestYearValue, unit as Unit, true, false).value,

            footer:
                !figureFooter || figureFooter.processed.trim() === ""
                    ? null
                    : {
                          processed: figureFooterProcessed,
                          value: figureFooterProcessed,
                          format: figureFooterProcessed
                      }
        };
    }

    const measure = kpis?.[0]?.[2] ?? latestYearData?.[0]?.indicatorTitle ?? "Line data";
    const lineDataSeriesName = provider?.name;

    const providerMedianSeriesName = getConnectionSizeMedianText(metadata, provider?.connectionSizeCategory);

    let values = _years.map(year => {
        const providerValue = extractReportDataFn(kpis, yearToResults?.[year], year);

        const baseValue = getBaseValue(providerValue.value, providerValue.unit);

        if (baseValue?.value != null) {
            unit = baseValue?.unit;
        }

        return baseValue?.value == null ? null : roundValue(baseValue?.value, baseValue?.unit, true, false)?.value;

        // return providerValue?.value == null ? null : roundValue(

        //     providerValue?.value, unit as Unit).value;
    });

    let averages = _years.map(year => {
        const values = conSizeProviders
            .map(d => {
                const yearProviderKey = yearProviderKeyFn(year, d?.tid);
                const providerValue = extractReportDataFn(kpis, yearProviderToAllResults[yearProviderKey], year);

                const baseValue = getBaseValue(providerValue.value, providerValue.unit);

                if (baseValue?.value != null) {
                    unit = baseValue?.unit;
                }

                return baseValue?.value == null
                    ? null
                    : roundValue(baseValue?.value, baseValue?.unit, true, false)?.value;

                // return providerValue?.value == null ? null : roundValue(providerValue?.value, unit as Unit).value;
            })
            .filter(d => d != null);

        return appMedian(values, unit as Unit);

        // if (!values?.length) return null;

        // const average = _.mean(values);
        // return roundValue(average ?? 0, unit as Unit).value;
    });

    // filter out years that at least value or average exists
    const yearToValueMap = _years.reduce((prev, cur, index) => {
        prev[cur] = values[index];
        return prev;
    }, {});
    const yearToAverageMap = _years.reduce((prev, cur, index) => {
        prev[cur] = averages[index];
        return prev;
    }, {});

    const goodStartYear = _years.find((__, index) => {
        return values[index] != null || averages[index] != null;
    });

    _years =
        _years === years
            ? _years
            : _.sortBy(
                  _.uniq([..._years.filter(year => year >= goodStartYear), previousYear, latestYear]),
                  d => d,
                  "asc"
              );

    // re-filter value or averages to match with the years
    values = _years.map(year => {
        return yearToValueMap[year];
    });

    averages = _years.map(year => {
        return yearToAverageMap[year];
    });

    // the first year that has data
    const initialYear = extractInitialYearWith1DimValues(_years, values, averages);

    const initialYearData = extractReportDataFn(kpis, yearToResults?.[initialYear], initialYear);

    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYearData?.year ?? 0,
        endYear: latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYearData?.year ?? 0,
        endYear: latestYear
    });

    // if (figure.value != null && !isNaN(parseFloat(`${figure.value}`))) {
    //     const round = roundValue(figure.value as number, unit);
    //     figure.value = round.value;
    //     figure.decimals = round.decimals;
    // }

    return {
        unit,
        year: latestYear,

        figure: !(visType === "figure" || visType === "figureWithSolidDashLine") ? null : figure,

        graph:
            visType === "figure"
                ? null
                : {
                      measure,
                      years: _years,
                      lineData: values ?? [],
                      providerMedianSeriesName,
                      providerMedianAnnotation: providerMedianSeriesName,
                      providerMedianData: averages ?? [],

                      lineDataSeriesName: `${lineDataSeriesName}`,
                      header:
                          !visHeader || visHeader.processed.trim() === ""
                              ? null
                              : {
                                    processed: visHeaderProcessed,
                                    value: visHeaderProcessed,
                                    format: visHeaderProcessed
                                },
                      footer:
                          !visFooter || visFooter.processed.trim() === ""
                              ? null
                              : {
                                    processed: visFooterProcessed,
                                    value: visFooterProcessed,
                                    format: visFooterProcessed
                                }
                  }
    };
};
